import React, { useState } from "react";
import PropTypes from "prop-types";

const Step5Summary = ({
  formData,
  error,
  article,
  loadingRubriques,
  getRubriqueName,
}) => {
  const [expandedSections, setExpandedSections] = useState({
    content: false,
    bibliography: false,
  });

  const hasContent = (obj) => {
    if (!obj) return false;
    return Object.values(obj).some((value) =>
      Array.isArray(value) ? value.length > 0 : value && value.trim() !== ""
    );
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const stripHtmlTags = (html) => {
    if (!html) return "";
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  const getContentPreview = (content, maxLength = 200) => {
    if (!content) return "Aucun contenu";
    const textContent = stripHtmlTags(content);
    return textContent.length > maxLength
      ? textContent.substring(0, maxLength) + "..."
      : textContent;
  };

  const getContentWordCount = (content) => {
    if (!content) return 0;
    const textContent = stripHtmlTags(content);
    return textContent.split(/\s+/).filter((word) => word.length > 0).length;
  };

  const getCompletionPercentage = () => {
    let completed = 0;
    let total = 10; // Augmenté pour inclure bibliographie et contenu

    if (formData.title_art?.fr) completed++;
    if (formData.resume_art?.fr) completed++;
    if (formData.key_words_art?.fr?.length > 0) completed++;
    if (formData.id_rub) completed++;
    if (formData.study_area_art) completed++;
    if (formData.authors_art?.length > 0) completed++;
    if (formData.coauthors_art?.length > 0) completed++;
    if (formData.doi_art && formData.doi_art !== "En cours d'attribution")
      completed++;
    if (formData.materiel_rech_art) completed++;
    if (hasContent(formData.corp_art)) completed++;
    if (formData.bibliography_art) completed++;
    if (formData.file_art || article?.file_art) completed++;

    return Math.round((completed / total) * 100);
  };

  const completionPercentage = getCompletionPercentage();

  return (
    <div className="step-content">
      <h3 className="mb-6 text-xl font-semibold text-gray-700">
        📋 Résumé et finalisation
      </h3>

      {/* Completion Progress */}
      <div className="mb-8 rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-6">
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-lg font-medium text-gray-800">
            Progression de l'article
          </h4>
          <span
            className={`text-2xl font-bold ${
              completionPercentage >= 80
                ? "text-green-600"
                : completionPercentage >= 60
                ? "text-yellow-600"
                : "text-red-600"
            }`}
          >
            {completionPercentage}%
          </span>
        </div>
        <div className="h-3 w-full rounded-full bg-gray-200">
          <div
            className={`h-3 rounded-full transition-all duration-500 ${
              completionPercentage >= 80
                ? "bg-green-500"
                : completionPercentage >= 60
                ? "bg-yellow-500"
                : "bg-red-500"
            }`}
            style={{ width: `${completionPercentage}%` }}
          ></div>
        </div>
        <p className="mt-2 text-sm text-gray-600">
          {completionPercentage >= 80
            ? "🎉 Article prêt pour publication !"
            : completionPercentage >= 60
            ? "⚠️ Quelques informations manquantes"
            : "📝 Veuillez compléter les informations essentielles"}
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4">
          <div className="flex">
            <svg
              className="mr-2 h-5 w-5 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Left Column - Article Information */}
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h4 className="mb-4 flex items-center text-lg font-medium text-gray-800">
              <svg
                className="mr-2 h-5 w-5 text-[#57A863]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Informations de base
            </h4>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-600">
                  Titre (FR):
                </span>
                <p className="mt-1 text-sm text-gray-800">
                  {formData.title_art?.fr || "Non défini"}
                </p>
              </div>
              {formData.title_art?.en && (
                <div>
                  <span className="text-sm font-medium text-gray-600">
                    Titre (EN):
                  </span>
                  <p className="mt-1 text-sm text-gray-800">
                    {formData.title_art.en}
                  </p>
                </div>
              )}
              {formData.title_art?.mg && (
                <div>
                  <span className="text-sm font-medium text-gray-600">
                    Titre (MG):
                  </span>
                  <p className="mt-1 text-sm text-gray-800">
                    {formData.title_art.mg}
                  </p>
                </div>
              )}
              <div>
                <span className="text-sm font-medium text-gray-600">
                  Catégorie:
                </span>
                <p className="mt-1 text-sm text-gray-800">
                  {loadingRubriques
                    ? "Chargement..."
                    : getRubriqueName(formData.id_rub) || "Non sélectionnée"}
                </p>
              </div>
              {formData.study_area_art && (
                <div>
                  <span className="text-sm font-medium text-gray-600">
                    Zone d'étude:
                  </span>
                  <p className="mt-1 text-sm text-gray-800">
                    {formData.study_area_art}
                  </p>
                </div>
              )}
              <div>
                <span className="text-sm font-medium text-gray-600">DOI:</span>
                <p className="mt-1 text-sm text-gray-800">{formData.doi_art}</p>
              </div>
            </div>
          </div>

          {/* Keywords */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h4 className="mb-4 flex items-center text-lg font-medium text-gray-800">
              <svg
                className="mr-2 h-5 w-5 text-[#57A863]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                />
              </svg>
              Mots-clés
            </h4>
            <div className="space-y-3">
              {["fr", "en", "mg"].map(
                (lang) =>
                  formData.key_words_art?.[lang]?.length > 0 && (
                    <div key={lang}>
                      <span className="text-sm font-medium text-gray-600">
                        {lang === "fr"
                          ? "Français"
                          : lang === "en"
                          ? "Anglais"
                          : "Malgache"}
                        :
                      </span>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {formData.key_words_art[lang].map((word, idx) => (
                          <span
                            key={idx}
                            className={`inline-flex items-center rounded-full px-2 py-1 text-xs ${
                              lang === "fr"
                                ? "bg-[#57A863]/10 text-[#57A863]"
                                : lang === "en"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-purple-100 text-purple-700"
                            }`}
                          >
                            {word}
                          </span>
                        ))}
                      </div>
                    </div>
                  )
              )}
              {!hasContent(formData.key_words_art) && (
                <p className="text-sm italic text-gray-500">
                  Aucun mot-clé défini
                </p>
              )}
            </div>
          </div>

          {/* Content Preview */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <div className="mb-4 flex items-center justify-between">
              <h4 className="flex items-center text-lg font-medium text-gray-800">
                <svg
                  className="mr-2 h-5 w-5 text-[#57A863]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Contenu de l'article
              </h4>
              <button
                type="button"
                onClick={() => toggleSection("content")}
                className="text-[#57A863] hover:text-[#449954] focus:outline-none"
              >
                {expandedSections.content ? (
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                )}
              </button>
            </div>

            <div className="space-y-3">
              {["fr", "en", "mg"].map((lang) => {
                const content = formData.corp_art?.[lang];
                const wordCount = getContentWordCount(content);

                if (!content || content.trim() === "") return null;

                return (
                  <div
                    key={lang}
                    className="border-l-4 border-[#57A863]/20 pl-4"
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600">
                        {lang === "fr"
                          ? "Français"
                          : lang === "en"
                          ? "Anglais"
                          : "Malgache"}
                        :
                      </span>
                      <span className="text-xs text-gray-500">
                        {wordCount} mots
                      </span>
                    </div>

                    {expandedSections.content ? (
                      <div
                        className="prose prose-sm max-w-none text-sm text-gray-800"
                        dangerouslySetInnerHTML={{ __html: content }}
                      />
                    ) : (
                      <p className="text-sm leading-relaxed text-gray-700">
                        {getContentPreview(content)}
                      </p>
                    )}
                  </div>
                );
              })}

              {!hasContent(formData.corp_art) && (
                <div className="py-8 text-center text-gray-500">
                  <svg
                    className="mx-auto mb-4 h-12 w-12 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <p className="text-sm italic">Aucun contenu rédigé</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Content and Files */}
        <div className="space-y-6">
          {/* Resume */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h4 className="mb-4 flex items-center text-lg font-medium text-gray-800">
              <svg
                className="mr-2 h-5 w-5 text-[#57A863]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h7"
                />
              </svg>
              Résumé
            </h4>
            <div className="space-y-3">
              {formData.resume_art?.fr && (
                <div>
                  <span className="text-sm font-medium text-gray-600">
                    Français:
                  </span>
                  <p className="mt-1 line-clamp-3 text-sm text-gray-800">
                    {formData.resume_art.fr}
                  </p>
                </div>
              )}
              {formData.resume_art?.en && (
                <div>
                  <span className="text-sm font-medium text-gray-600">
                    Anglais:
                  </span>
                  <p className="mt-1 line-clamp-3 text-sm text-gray-800">
                    {formData.resume_art.en}
                  </p>
                </div>
              )}
              {formData.resume_art?.mg && (
                <div>
                  <span className="text-sm font-medium text-gray-600">
                    Malgache:
                  </span>
                  <p className="mt-1 line-clamp-3 text-sm text-gray-800">
                    {formData.resume_art.mg}
                  </p>
                </div>
              )}
              {!hasContent(formData.resume_art) && (
                <p className="text-sm italic text-gray-500">
                  Aucun résumé défini
                </p>
              )}
            </div>
          </div>

          {/* Bibliography */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <div className="mb-4 flex items-center justify-between">
              <h4 className="flex items-center text-lg font-medium text-gray-800">
                <svg
                  className="mr-2 h-5 w-5 text-[#57A863]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
                Bibliographie
              </h4>
              <button
                type="button"
                onClick={() => toggleSection("bibliography")}
                className="text-[#57A863] hover:text-[#449954] focus:outline-none"
              >
                {expandedSections.bibliography ? (
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                )}
              </button>
            </div>

            {formData.bibliography_art ? (
              <div className="space-y-2">
                {expandedSections.bibliography ? (
                  <div className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded border bg-gray-50 p-4 text-sm text-gray-800">
                    {formData.bibliography_art}
                  </div>
                ) : (
                  <div className="text-sm text-gray-700">
                    <p className="line-clamp-3">
                      {formData.bibliography_art.substring(0, 200)}
                      {formData.bibliography_art.length > 200 && "..."}
                    </p>
                    <div className="mt-2 flex items-center text-xs text-gray-500">
                      <svg
                        className="mr-1 h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      {
                        formData.bibliography_art
                          .split("\n")
                          .filter((line) => line.trim()).length
                      }{" "}
                      références
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-6 text-center text-gray-500">
                <svg
                  className="mx-auto mb-2 h-8 w-8 text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
                <p className="text-sm italic">Aucune bibliographie ajoutée</p>
              </div>
            )}
          </div>

          {/* Files and Research Material */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h4 className="mb-4 flex items-center text-lg font-medium text-gray-800">
              <svg
                className="mr-2 h-5 w-5 text-[#57A863]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                />
              </svg>
              Fichiers et matériel
            </h4>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-600">
                  Fichier PDF:
                </span>
                <div className="mt-1 flex items-center">
                  {formData.file_art ? (
                    <div className="flex items-center rounded bg-green-50 px-2 py-1 text-sm text-green-700">
                      <svg
                        className="mr-1 h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Nouveau: {formData.file_art.name}
                    </div>
                  ) : article?.file_art ? (
                    <div className="flex items-center rounded bg-blue-50 px-2 py-1 text-sm text-blue-700">
                      <svg
                        className="mr-1 h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      Actuel: {article.file_art.split("/").pop()}
                    </div>
                  ) : (
                    <span className="text-sm italic text-gray-500">
                      Aucun fichier
                    </span>
                  )}
                </div>
              </div>

              {formData.materiel_rech_art && (
                <div>
                  <span className="text-sm font-medium text-gray-600">
                    Matériel de recherche:
                  </span>
                  <p className="mt-1 line-clamp-2 text-sm text-gray-800">
                    {formData.materiel_rech_art}
                  </p>
                </div>
              )}

              {formData.research_material_url && (
                <div>
                  <span className="text-sm font-medium text-gray-600">
                    URL IDS:
                  </span>
                  <a
                    href={formData.research_material_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 block text-sm text-[#57A863] hover:underline"
                  >
                    {formData.research_material_url}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Step5Summary.propTypes = {
  formData: PropTypes.shape({
    title_art: PropTypes.object,
    resume_art: PropTypes.object,
    key_words_art: PropTypes.object,
    corp_art: PropTypes.object,
    bibliography_art: PropTypes.string,
    id_rub: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    study_area_art: PropTypes.string,
    doi_art: PropTypes.string,
    materiel_rech_art: PropTypes.string,
    research_material_url: PropTypes.string,
    file_art: PropTypes.object,
  }).isRequired,
  error: PropTypes.string,
  article: PropTypes.object,
  loadingRubriques: PropTypes.bool,
  getRubriqueName: PropTypes.func.isRequired,
};

export default Step5Summary;
